import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { format, startOfMonth, endOfMonth, subMonths, addMonths } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { paginateSupabase } from '@/lib/supabase-paginate';
import { convertAsOfForAccount, RateRow } from '@/lib/fx';
import { mean, projectTrend } from './forecastMath';

const BASE_CCY = 'ETB';
const LOOKBACK = 6; // trailing complete months used to fit the trend
const HORIZON = 3; // months projected forward (current month + next 2)

export type ForecastCurrency = 'ETB' | 'USD';

export interface ForecastGroup {
  label: string; // cost-group name
  runRate: number; // avg monthly spend over the basis window (reference baseline)
  forecast: number[]; // per forecast month (trend-projected, clamped ≥ 0)
  total: number; // sum across forecast months
}

export interface ExpenseForecastResult {
  basisMonths: { key: string; label: string }[]; // the 3 complete months averaged
  forecastMonths: { key: string; label: string }[]; // the 3 projected months
  groups: ForecastGroup[]; // operating cost-groups, desc by run-rate
  totalByMonth: number[]; // total forecast per month
  grandTotal: number;
}

interface CategoryNode {
  name: string;
  parent_id: string | null;
  category_type: string | null;
  cash_flow_type: string | null;
}

/**
 * Upcoming operating-expense forecast from historical patterns.
 *
 * For each operating cost-group we fit a least-squares linear trend over the
 * trailing `LOOKBACK` complete months and extend it across the next `HORIZON`
 * months (clamped ≥ 0), so each month follows the group's recent trajectory
 * rather than repeating a flat average. `runRate` (the plain average) is kept
 * as a reference baseline. The current partial month is excluded from the basis.
 * Independent of the report's date filter — always the same trailing window.
 */
export function useExpenseForecast(displayCurrency: ForecastCurrency = 'ETB') {
  return useQuery({
    queryKey: ['expense-forecast', displayCurrency],
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<ExpenseForecastResult> => {
      const now = new Date();
      // Basis = last LOOKBACK complete months (exclude the current partial month).
      const basisStart = format(startOfMonth(subMonths(now, LOOKBACK)), 'yyyy-MM-dd');
      const basisEnd = format(endOfMonth(subMonths(now, 1)), 'yyyy-MM-dd');
      const basisMonths = Array.from({ length: LOOKBACK }, (_, i) => {
        const d = subMonths(now, LOOKBACK - i); // oldest → newest
        return { key: format(d, 'yyyy-MM'), label: format(d, 'MMM yyyy') };
      });
      const basisKeys = new Set(basisMonths.map((m) => m.key));
      const forecastMonths = Array.from({ length: HORIZON }, (_, i) => {
        const d = addMonths(now, i); // current month + next
        return { key: format(d, 'yyyy-MM'), label: format(d, 'MMM yyyy') };
      });

      const [expsRes, catsRes, ratesRes, banksRes] = await Promise.all([
        paginateSupabase<any>((from, to) =>
          (supabase as any)
            .from('expenses')
            .select('id,expense_date,amount,total_amount,net_payment,currency,category_id,bank_account_id')
            .in('payment_status', ['paid', 'partially_paid'])
            .gte('expense_date', basisStart)
            .lte('expense_date', basisEnd)
            .range(from, to)
        ),
        (supabase as any)
          .from('expense_categories')
          .select('id,name,parent_id,category_type,cash_flow_type')
          .limit(10000),
        paginateSupabase<any>((from, to) =>
          (supabase as any)
            .from('exchange_rates')
            .select('currency_pair,rate,effective_date,bank_account_id,rate_type')
            .order('effective_date', { ascending: false })
            .range(from, to)
        ),
        (supabase as any)
          .from('bank_accounts')
          .select('id,exchange_rate_type')
          .limit(10000),
      ]);

      const rates: RateRow[] = (ratesRes || []) as RateRow[];
      const bankRateType = new Map<string, string>();
      for (const b of banksRes.data || []) bankRateType.set(b.id, b.exchange_rate_type || 'bank_rate');

      // Per-transaction, account-aware conversion to the display currency
      // (normalise to ETB first, then ETB→USD if needed) — mirrors the reports.
      const toDisplay = (amount: number, ccy: string | null, date: string, bankAccountId?: string | null): number => {
        const from = (ccy || BASE_CCY).toUpperCase();
        let etb = amount;
        if (from !== BASE_CCY) {
          const c = convertAsOfForAccount(amount, from, BASE_CCY, date, rates, bankAccountId);
          etb = c ? c.converted : amount;
        }
        if (displayCurrency === 'ETB') return etb;
        const c2 = convertAsOfForAccount(etb, BASE_CCY, 'USD', date, rates, bankAccountId);
        return c2 ? c2.converted : etb;
      };

      const catMap = new Map<string, CategoryNode>();
      for (const c of catsRes.data || []) {
        catMap.set(c.id, { name: c.name, parent_id: c.parent_id, category_type: c.category_type, cash_flow_type: c.cash_flow_type });
      }
      // Walk to the cost-group ancestor (matches useCashFlow.getExpenseLabel).
      const groupLabel = (categoryId: string | null): string => {
        if (!categoryId) return 'Other Expenses';
        let cur = catMap.get(categoryId);
        while (cur) {
          if (cur.category_type === 'cost_group') return cur.name;
          if (cur.parent_id) {
            const parent = catMap.get(cur.parent_id);
            if (parent && parent.category_type === 'cost_group') return parent.name;
            cur = parent || undefined;
          } else break;
        }
        return catMap.get(categoryId)?.name || 'Other Expenses';
      };
      // Nearest ancestor's cash_flow_type (default operating), to keep only opex.
      const cashFlowType = (categoryId: string | null): string => {
        let cur = categoryId ? catMap.get(categoryId) : undefined;
        while (cur) {
          if (cur.cash_flow_type) return cur.cash_flow_type;
          cur = cur.parent_id ? catMap.get(cur.parent_id) : undefined;
        }
        return 'operating';
      };

      // Aggregate operating spend per cost-group per basis month.
      const byGroup = new Map<string, Record<string, number>>();
      for (const e of expsRes || []) {
        if (cashFlowType(e.category_id) !== 'operating') continue;
        const key = format(new Date(e.expense_date), 'yyyy-MM');
        if (!basisKeys.has(key)) continue;
        const amt = Number(e.net_payment ?? e.total_amount ?? e.amount ?? 0);
        if (!amt) continue;
        const converted = toDisplay(amt, e.currency, e.expense_date, e.bank_account_id);
        const label = groupLabel(e.category_id);
        const m = byGroup.get(label) || {};
        m[key] = (m[key] || 0) + converted;
        byGroup.set(label, m);
      }

      const groups: ForecastGroup[] = [...byGroup.entries()]
        .map(([label, monthly]) => {
          // One value per basis month (0 where the group had no spend); extend
          // its trend forward so each month follows the recent trajectory.
          const ys = basisMonths.map((mo) => monthly[mo.key] || 0);
          const forecast = projectTrend(ys, HORIZON);
          return {
            label,
            runRate: mean(ys),
            forecast,
            total: forecast.reduce((s, v) => s + v, 0),
          };
        })
        .filter((g) => g.runRate > 0.005 || g.total > 0.005)
        .sort((a, b) => b.total - a.total);

      const totalByMonth = forecastMonths.map((_, i) => groups.reduce((s, g) => s + g.forecast[i], 0));
      const grandTotal = totalByMonth.reduce((s, v) => s + v, 0);

      return { basisMonths, forecastMonths, groups, totalByMonth, grandTotal };
    },
  });
}
